/* eslint-disable react/prop-types */
// Shell du cockpit directeur — portage du handoff Claude Design.
//
// Reproduit le châssis du design (Directeur/Sidebar.jsx + Directeur/Header.jsx)
// plutôt que le chrome générique de l'app (`Layout` + `Sidebar` + `Topbar`) :
// sidebar 240px sur fond #FAFAF8, header 56px blanc, contenu sur #F8F9FB.
//
// Monté une seule fois par une route parente : la sidebar et le header restent
// montés, seul <Outlet/> change à la navigation (le design est un cockpit, pas
// une suite de pages indépendantes).
//
// Écarts assumés vis-à-vis du design, tous dans le sens du produit réel :
//  - couleurs d'accent via les tokens `--color-accent*` (mêmes valeurs que le
//    design, #F97316, mais personnalisables par établissement) ;
//  - données réelles (établissement, utilisateur, pastilles) au lieu des
//    valeurs figées du design ;
//  - sidebar repliable en drawer sous `md` (le design est desktop only).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LogOut,
  Search,
  Bell,
  Plus,
  ChevronDown,
  Menu,
  X,
  Send,
  AlertTriangle,
  CalendarCheck,
  FileText,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { getDirecteurSidebarCounts } from "../../api/directeurApi";
import DemoModeBanner from "../ui/DemoModeBanner";
import NotifPanel, { useDirecteurNotifications } from "./NotifPanel";
import { DIRECTEUR_NAV, DIRECTEUR_NAV_FLAT, DIRECTEUR_PAGE_TITLES } from "./nav";

// Tokens du design (Sidebar.jsx / Header.jsx). L'orange passe par `bg-accent`.
const SIDEBAR_BG = "#FAFAF8";
const SIDEBAR_BORDER = "#EDE9E3";
const CHIP_BG = "#F4F1ED";
const INK = "#2F2F2F";
const MUTED = "#B0A99E";
const PAGE_BG = "#F8F9FB";
const AVATAR_BG = "#1E3A8A";

const SIDEBAR_WIDTH = 240;
const BAR_HEIGHT = 56;

/** Initiales — « Léo Clement » donne « LC », repli sur le username. */
function initialsOf(user) {
  const parts = [user?.first_name, user?.last_name].filter(Boolean);
  if (parts.length) return parts.map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  return (user?.username || "?").slice(0, 2).toUpperCase();
}

function displayName(user) {
  const full = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();
  return full || user?.username || "Directeur";
}

/** Normalisation pour la recherche rapide (casse + accents). */
function fold(s) {
  return (s || "").toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
}

/* ─────────────────────────── Sidebar ─────────────────────────── */

function SidebarNav({ badges, user, onNavigate, onLogout }) {
  const etabNom = user?.etablissement_nom || "Mon établissement";
  const etabVille = user?.etablissement_ville;

  return (
    <div
      className="flex h-full flex-col"
      style={{
        width: SIDEBAR_WIDTH,
        minWidth: SIDEBAR_WIDTH,
        background: SIDEBAR_BG,
        borderRight: `1px solid ${SIDEBAR_BORDER}`,
      }}
    >
      {/* Logo */}
      <Link
        to="/directeur"
        onClick={onNavigate}
        className="flex shrink-0 items-center gap-2.5 px-4"
        style={{ height: BAR_HEIGHT, borderBottom: `1px solid ${SIDEBAR_BORDER}` }}
      >
        <img src="/logo_SchoolSenses.png" alt="SchoolSenses" className="h-7 w-7 rounded-lg object-contain" />
        <span className="text-sm font-extrabold tracking-tight text-accent">SchoolSenses</span>
      </Link>

      {/* Contexte établissement */}
      <div
        className="shrink-0"
        style={{ padding: "9px 14px", borderBottom: `1px solid ${SIDEBAR_BORDER}`, background: CHIP_BG }}
      >
        <div style={{ fontSize: 11, fontWeight: 600, color: INK }} className="truncate" title={etabNom}>
          {etabNom}
        </div>
        <div style={{ fontSize: 10, color: MUTED, marginTop: 1 }} className="truncate">
          {etabVille ? `${etabVille} · Directeur` : "Directeur"}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-2">
        {DIRECTEUR_NAV.map((section, si) => (
          <div key={section.title || `s${si}`}>
            {section.title && (
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: MUTED,
                  textTransform: "uppercase",
                  letterSpacing: ".10em",
                  padding: "0 8px",
                  margin: "12px 0 3px",
                }}
              >
                {section.title}
              </div>
            )}
            {section.items.map((item) => {
              const count = item.badge ? badges[item.badge] || 0 : 0;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  style={{ padding: "7px 10px", fontSize: 12.5, fontWeight: 500, marginBottom: 1 }}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-lg transition-colors ${
                      isActive ? "bg-accent text-white" : "text-gray-600 hover:bg-[rgba(249,115,22,0.07)]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon size={15} className={`shrink-0 ${isActive ? "text-white" : "text-gray-400"}`} />
                      <span className="flex-1 truncate">{item.label}</span>
                      {count > 0 && (
                        <span
                          className={isActive ? "bg-white/25" : "bg-accent"}
                          style={{
                            color: "white",
                            fontSize: 10,
                            fontWeight: 700,
                            padding: "1px 6px",
                            borderRadius: 999,
                          }}
                          aria-label={`${count} à traiter`}
                        >
                          {count > 99 ? "99+" : count}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Utilisateur */}
      <div
        className="flex shrink-0 items-center gap-2.5"
        style={{ padding: "10px 12px", borderTop: `1px solid ${SIDEBAR_BORDER}`, background: CHIP_BG }}
      >
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
          style={{ background: AVATAR_BG }}
        >
          {initialsOf(user)}
        </span>
        <div className="min-w-0 flex-1">
          <div style={{ fontSize: 12, fontWeight: 600, color: INK }} className="truncate">
            {displayName(user)}
          </div>
          <div style={{ fontSize: 10, color: MUTED }}>Directeur</div>
        </div>
        <button
          type="button"
          onClick={onLogout}
          title="Se déconnecter"
          aria-label="Se déconnecter"
          className="rounded p-1 text-gray-400 transition-colors hover:bg-white hover:text-gray-600"
        >
          <LogOut size={14} />
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────── Header ─────────────────────────── */

const QUICK_ACTIONS = [
  { label: "Envoyer une annonce", icon: Send, to: "/directeur/communication" },
  { label: "Signaler un incident", icon: AlertTriangle, to: "/directeur/attendance" },
  { label: "Planifier une réunion", icon: CalendarCheck, to: "/directeur/events" },
  { label: "Ajouter un document", icon: FileText, to: "/directeur/documents" },
];

function DirecteurHeader({ title, sub, notifs, user, onOpenMenu }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [showActions, setShowActions] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const actionsRef = useRef(null);
  const notifsRef = useRef(null);
  const notifCount = notifs.nonLues;

  // Fermeture du menu « Action rapide » au clic extérieur / Échap.
  useEffect(() => {
    if (!showActions) return undefined;
    const onDocClick = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) setShowActions(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setShowActions(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [showActions]);

  // Idem pour le panneau de notifications (le panneau gère Échap lui-même).
  useEffect(() => {
    if (!showNotifs) return undefined;
    const onDocClick = (e) => {
      if (notifsRef.current && !notifsRef.current.contains(e.target)) setShowNotifs(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showNotifs]);

  // Recherche rapide = accès direct à une page du cockpit par son intitulé.
  const onSearch = (e) => {
    e.preventDefault();
    const q = fold(search).trim();
    if (!q) return;
    const hit = DIRECTEUR_NAV_FLAT.find((i) => fold(i.label).includes(q));
    if (hit) {
      setSearch("");
      navigate(hit.to);
    }
  };

  return (
    <header
      className="relative z-20 flex shrink-0 items-center gap-3.5 bg-white px-4 sm:px-6"
      style={{ height: BAR_HEIGHT, borderBottom: "1px solid #E5E7EB" }}
    >
      <button
        type="button"
        onClick={onOpenMenu}
        aria-label="Ouvrir le menu"
        className="-ml-1 rounded-lg p-1.5 text-gray-500 hover:bg-gray-50 md:hidden"
      >
        <Menu size={18} />
      </button>

      <div className="flex min-w-0 flex-1 items-baseline gap-2.5">
        <span style={{ fontSize: 15, fontWeight: 700, color: INK }} className="whitespace-nowrap">
          {title}
        </span>
        <span style={{ fontSize: 12 }} className="truncate text-gray-400">
          {sub}
        </span>
      </div>

      <form onSubmit={onSearch} className="relative hidden lg:block" style={{ width: 210 }}>
        <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Recherche rapide…"
          aria-label="Recherche rapide"
          className="w-full rounded-lg border border-gray-200 pl-7 pr-2.5 text-xs text-gray-800 outline-none transition-colors focus:border-accent"
          style={{ height: 32, background: PAGE_BG }}
        />
      </form>

      <div className="relative" ref={notifsRef}>
        <button
          type="button"
          onClick={() => setShowNotifs((p) => !p)}
          aria-expanded={showNotifs}
          title={notifCount > 0 ? `${notifCount} notification(s) non lue(s)` : "Aucune notification non lue"}
          className={`relative rounded-lg p-1.5 transition-colors ${
            showNotifs ? "bg-[#F8F9FB] text-accent" : "text-gray-500 hover:bg-gray-50"
          }`}
        >
          <Bell size={18} />
          {notifCount > 0 && (
            <span
              className="absolute right-0.5 top-0.5 flex items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white"
              style={{ width: 14, height: 14 }}
            >
              {notifCount > 9 ? "9+" : notifCount}
            </span>
          )}
        </button>
        {showNotifs && (
          <NotifPanel
            items={notifs.items}
            lues={notifs.lues}
            nonLues={notifs.nonLues}
            onRead={notifs.marquerLu}
            onReadAll={notifs.toutMarquerLu}
            onClose={() => setShowNotifs(false)}
          />
        )}
      </div>

      <div className="relative" ref={actionsRef}>
        <button
          type="button"
          onClick={() => setShowActions((p) => !p)}
          aria-expanded={showActions}
          className="flex items-center gap-1.5 rounded-lg bg-accent px-3 text-xs font-semibold text-white transition-colors hover:bg-accent-dark"
          style={{ height: 32 }}
        >
          <Plus size={13} />
          <span className="hidden sm:inline">Action rapide</span>
          <ChevronDown size={12} className="text-white/70" />
        </button>
        {showActions && (
          <div
            className="absolute right-0 z-50 rounded-[10px] border border-gray-200 bg-white p-1.5 shadow-xl"
            style={{ top: 38, minWidth: 210 }}
          >
            {QUICK_ACTIONS.map((a) => (
              <button
                key={a.to}
                type="button"
                onClick={() => {
                  setShowActions(false);
                  navigate(a.to);
                }}
                className="flex w-full items-center gap-2.5 rounded-[7px] px-3 py-2 text-left text-[13px] font-medium text-gray-700 transition-colors hover:bg-[#F8F9FB]"
              >
                <a.icon size={14} className="text-gray-400" />
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <span
        title={displayName(user)}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
        style={{ background: AVATAR_BG }}
      >
        {initialsOf(user)}
      </span>
    </header>
  );
}

/* ─────────────────────────── Shell ─────────────────────────── */

export default function DirecteurShell() {
  const { user, logout } = useAuth() || {};
  const location = useLocation();
  const navigate = useNavigate();
  const [badges, setBadges] = useState({ attendance: 0, communication: 0, hr: 0 });
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Notifications de la cloche — chargées une seule fois au montage du shell.
  const notifs = useDirecteurNotifications();

  // Pastilles du menu — rechargées à chaque changement de page (elles bougent
  // au fil des actions du directeur). Échec silencieux : le menu reste utilisable.
  useEffect(() => {
    let alive = true;
    getDirecteurSidebarCounts()
      .then((d) => {
        if (!alive || !d) return;
        setBadges({
          attendance: Number(d.attendance) || 0,
          communication: Number(d.communication) || 0,
          hr: Number(d.hr) || 0,
        });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [location.pathname]);

  // Le drawer mobile se referme à chaque navigation.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  const { title, sub } = useMemo(() => {
    const meta = DIRECTEUR_PAGE_TITLES[location.pathname];
    if (!meta) return { title: "Directeur", sub: "" };
    if (location.pathname === "/directeur") {
      const jour = new Date().toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      return { title: meta.title, sub: `${meta.sub} · ${jour}` };
    }
    return meta;
  }, [location.pathname]);

  const onLogout = useCallback(() => {
    logout?.();
    navigate("/login");
  }, [logout, navigate]);

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: PAGE_BG }}>
      {/* Sidebar desktop */}
      <div className="hidden md:block">
        <SidebarNav badges={badges} user={user} onLogout={onLogout} />
      </div>

      {/* Sidebar mobile (drawer) */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <SidebarNav badges={badges} user={user} onLogout={onLogout} onNavigate={() => setDrawerOpen(false)} />
          <button
            type="button"
            aria-label="Fermer le menu"
            onClick={() => setDrawerOpen(false)}
            className="flex flex-1 items-start justify-start bg-black/40 p-4"
          >
            <X size={18} className="text-white" />
          </button>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <DirecteurHeader
          title={title}
          sub={sub}
          notifs={notifs}
          user={user}
          onOpenMenu={() => setDrawerOpen(true)}
        />
        <main className="flex-1 overflow-y-auto" style={{ background: PAGE_BG }}>
          <div className="px-4 pb-12 pt-5 sm:px-7 sm:pt-6">
            <DemoModeBanner />
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
